import React, { useState, useRef } from 'react';
import { Bold, Italic, Underline, Type, List, ListOrdered, Trash2 } from 'lucide-react';

const TextBlock = ({ block, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  const blockFormatting = block.formatting || {
    level: 'p',
    fontFamily: 'sans-serif',
    listType: 'none'
  };

  const segments = block.segments || [];

  const handleContentChange = (e) => {
    const newText = e.target.value;
    const newSegments = [{ text: newText, formatting: {} }];
    onUpdate(block.id, { segments: newSegments, content: newText });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const indent = '    ';

      let newText;
      if (start !== end) {
        const selectedText = text.substring(start, end);
        const linesInSelection = selectedText.split('\n');
        const indentedLines = linesInSelection.map(line => indent + line).join('\n');
        newText = text.substring(0, start) + indentedLines + text.substring(end);
      } else {
        newText = text.substring(0, start) + indent + text.substring(start);
      }

      const newSegments = [{ text: newText, formatting: {} }];
      onUpdate(block.id, { segments: newSegments, content: newText });

      setTimeout(() => {
        textarea.focus();
        const newCursorPos = start + indent.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const handleSelectionChange = () => {
    if (textareaRef.current) {
      setSelection({
        start: textareaRef.current.selectionStart,
        end: textareaRef.current.selectionEnd
      });
    }
  };

  const applyFormatToSelection = (formatKey, formatValue) => {
    const { start, end } = selection;
    const text = block.content || '';

    if (start === end) return;

    // Build new segments by merging with existing ones
    let currentPos = 0;
    const newSegments = [];
    const existingSegments = segments.length > 0 ? segments : [{ text, formatting: {} }];

    for (const segment of existingSegments) {
      const segmentStart = currentPos;
      const segmentEnd = currentPos + segment.text.length;

      // No overlap
      if (segmentEnd <= start || segmentStart >= end) {
        newSegments.push(segment);
      } else {
        // Partial or full overlap
        const beforeOverlapStart = Math.max(0, start - segmentStart);
        const beforeOverlapEnd = Math.min(segment.text.length, Math.max(0, start - segmentStart));
        const overlapStart = Math.max(0, start - segmentStart);
        const overlapEnd = Math.min(segment.text.length, end - segmentStart);
        const afterOverlapStart = Math.min(segment.text.length, Math.max(0, end - segmentStart));

        // Before part (no formatting change)
        if (overlapStart > 0) {
          newSegments.push({
            text: segment.text.substring(0, overlapStart),
            formatting: segment.formatting
          });
        }

        // Overlapped part (add formatting)
        if (overlapEnd > overlapStart) {
          const overlappedText = segment.text.substring(overlapStart, overlapEnd);
          newSegments.push({
            text: overlappedText,
            formatting: { ...segment.formatting, [formatKey]: formatValue }
          });
        }

        // After part (no formatting change)
        if (afterOverlapStart < segment.text.length) {
          newSegments.push({
            text: segment.text.substring(afterOverlapStart),
            formatting: segment.formatting
          });
        }
      }

      currentPos += segment.text.length;
    }

    onUpdate(block.id, { segments: newSegments, content: text });
    
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start, end);
      }
    }, 0);
  };

  const toggleFormatOnSelection = (key) => {
    applyFormatToSelection(key, true);
  };

  const handleColorChange = (color) => {
    const { start, end } = selection;
    const text = block.content || '';

    if (start === end) return;

    // Build new segments by merging with existing ones
    let currentPos = 0;
    const newSegments = [];
    const existingSegments = segments.length > 0 ? segments : [{ text, formatting: {} }];

    for (const segment of existingSegments) {
      const segmentStart = currentPos;
      const segmentEnd = currentPos + segment.text.length;

      // No overlap
      if (segmentEnd <= start || segmentStart >= end) {
        newSegments.push(segment);
      } else {
        // Partial or full overlap
        const overlapStart = Math.max(0, start - segmentStart);
        const overlapEnd = Math.min(segment.text.length, end - segmentStart);
        const afterOverlapStart = Math.min(segment.text.length, Math.max(0, end - segmentStart));

        // Before part (no formatting change)
        if (overlapStart > 0) {
          newSegments.push({
            text: segment.text.substring(0, overlapStart),
            formatting: segment.formatting
          });
        }

        // Overlapped part (add color)
        if (overlapEnd > overlapStart) {
          const overlappedText = segment.text.substring(overlapStart, overlapEnd);
          newSegments.push({
            text: overlappedText,
            formatting: { ...segment.formatting, color }
          });
        }

        // After part (no formatting change)
        if (afterOverlapStart < segment.text.length) {
          newSegments.push({
            text: segment.text.substring(afterOverlapStart),
            formatting: segment.formatting
          });
        }
      }

      currentPos += segment.text.length;
    }

    onUpdate(block.id, { segments: newSegments, content: text });
    
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start, end);
      }
    }, 0);
  };

  const handleToolbarMouseDown = (e, callback) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Capture selection before focus is lost
    if (textareaRef.current) {
      setSelection({
        start: textareaRef.current.selectionStart,
        end: textareaRef.current.selectionEnd
      });
    }
    
    callback();
    
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleBlockFormatChange = (key, value) => {
    onUpdate(block.id, {
      formatting: { ...blockFormatting, [key]: value }
    });
  };

  const getBlockClass = () => {
    switch (blockFormatting.level) {
      case 'h1':
        return 'text-h1';
      case 'h2':
        return 'text-h2';
      case 'h3':
        return 'text-h3';
      case 'p':
      default:
        return 'text-p';
    }
  };

  const renderStyledContent = () => {
    const content = block.content || '';
    const lines = content.split('\n');

    if (blockFormatting.listType === 'ordered') {
      return (
        <ol style={{ fontFamily: blockFormatting.fontFamily }}>
          {lines.map((line, i) => (
            <li key={i}>{line || '\u00A0'}</li>
          ))}
        </ol>
      );
    } else if (blockFormatting.listType === 'bullet') {
      return (
        <ul style={{ fontFamily: blockFormatting.fontFamily }}>
          {lines.map((line, i) => (
            <li key={i}>{line || '\u00A0'}</li>
          ))}
        </ul>
      );
    }

    if (segments.length === 0) {
      return (
        <span style={{ fontFamily: blockFormatting.fontFamily }}>
          {content || 'Click to add text...'}
        </span>
      );
    }

    return (
      <span style={{ fontFamily: blockFormatting.fontFamily }}>
        {segments.map((segment, idx) => {
          const style = {
            fontWeight: segment.formatting.bold ? 'bold' : 'normal',
            fontStyle: segment.formatting.italic ? 'italic' : 'normal',
            color: segment.formatting.color || 'inherit',
          };

          if (segment.formatting.underline && segment.formatting.strikethrough) {
            style.textDecoration = 'underline line-through';
          } else if (segment.formatting.underline) {
            style.textDecoration = 'underline';
          } else if (segment.formatting.strikethrough) {
            style.textDecoration = 'line-through';
          }

          return (
            <span key={idx} style={style}>
              {segment.text}
            </span>
          );
        })}
      </span>
    );
  };

  return (
    <div className="text-block">
      <div className="text-toolbar">
        <select
          value={blockFormatting.level || 'p'}
          onChange={(e) => {
            handleBlockFormatChange('level', e.target.value);
            textareaRef.current?.focus();
          }}
          className="level-select"
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>

        <select
          value={blockFormatting.fontFamily || 'sans-serif'}
          onChange={(e) => {
            handleBlockFormatChange('fontFamily', e.target.value);
            textareaRef.current?.focus();
          }}
          className="font-select"
        >
          <option value="sans-serif">Sans Serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
          <option value="georgia">Georgia</option>
          <option value="arial">Arial</option>
          <option value="verdana">Verdana</option>
        </select>

        <input
          type="color"
          onMouseDown={(e) => {
            e.preventDefault();
            if (textareaRef.current) {
              setSelection({
                start: textareaRef.current.selectionStart,
                end: textareaRef.current.selectionEnd
              });
            }
          }}
          onChange={(e) => {
            handleColorChange(e.target.value);
            textareaRef.current?.focus();
          }}
          className="color-picker"
          title="Text color (select text first)"
          defaultValue="#000000"
        />

        <button
          onMouseDown={(e) => handleToolbarMouseDown(e, () => toggleFormatOnSelection('bold'))}
          className="format-btn"
          title="Bold (select text first)"
        >
          <Bold size={16} />
        </button>

        <button
          onMouseDown={(e) => handleToolbarMouseDown(e, () => toggleFormatOnSelection('italic'))}
          className="format-btn"
          title="Italic (select text first)"
        >
          <Italic size={16} />
        </button>

        <button
          onMouseDown={(e) => handleToolbarMouseDown(e, () => toggleFormatOnSelection('underline'))}
          className="format-btn"
          title="Underline (select text first)"
        >
          <Underline size={16} />
        </button>

        <button
          onMouseDown={(e) => handleToolbarMouseDown(e, () => toggleFormatOnSelection('strikethrough'))}
          className="format-btn"
          title="Strikethrough (select text first)"
        >
          <s>S</s>
        </button>

        <div className="list-divider"></div>

        <button
          onMouseDown={(e) => handleToolbarMouseDown(e, () => handleBlockFormatChange('listType', blockFormatting.listType === 'bullet' ? 'none' : 'bullet'))}
          className={`format-btn ${blockFormatting.listType === 'bullet' ? 'active' : ''}`}
          title="Bullet list"
        >
          <List size={16} />
        </button>

        <button
          onMouseDown={(e) => handleToolbarMouseDown(e, () => handleBlockFormatChange('listType', blockFormatting.listType === 'ordered' ? 'none' : 'ordered'))}
          className={`format-btn ${blockFormatting.listType === 'ordered' ? 'active' : ''}`}
          title="Numbered list"
        >
          <ListOrdered size={16} />
        </button>
      </div>

      {isEditing ? (
        <textarea
          ref={textareaRef}
          value={block.content || ''}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          onBlur={() => setIsEditing(false)}
          onMouseUp={handleSelectionChange}
          onKeyUp={handleSelectionChange}
          onTouchEnd={handleSelectionChange}
          placeholder={blockFormatting.listType !== 'none' ? 'Enter each item on a new line...' : 'Enter your text here...'}
          className="text-input"
          autoFocus
          style={{ fontFamily: blockFormatting.fontFamily }}
        />
      ) : (
        <div
          onClick={() => {
            setIsEditing(true);
            setTimeout(() => textareaRef.current?.focus(), 0);
          }}
          className={`text-display ${getBlockClass()}`}
        >
          {renderStyledContent()}
        </div>
      )}
    </div>
  );
};

export default TextBlock;
